import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Pagination,
  Menu,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  Tooltip,
  Button,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { formatDateTimeMonth } from "../../../../../../../../../utils/functions";
import { thinDarkScrollbar } from "../../../../../../../../../utils/scrollbarStyles";
import { AccountCircleOutlined } from "@mui/icons-material";

interface NotesListProps {
  notes: any[];
  page: number;
  itemsPerPage: number;
  updateNote: (note: any) => void;
  setActivePage: (page: number) => void;
  homeSiteId: string;
  userId: string;
  title: string;
  divisionCode: string;
  opportunityId: string;
  isDirty?: (dirty: boolean) => void;
  showSnackbar: (
    message: string,
    severity: "success" | "error" | "info"
  ) => void;
}

const NotesList: React.FC<NotesListProps> = ({
  notes,
  page,
  itemsPerPage,
  updateNote,
  setActivePage,
  homeSiteId,
  userId,
  title,
  divisionCode,
  opportunityId,
  isDirty,
  showSnackbar,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [currentNoteId, setCurrentNoteId] = React.useState<string | null>(null);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const totalPages = Math.ceil(notes.length / itemsPerPage);
  const MAX_DESCRIPTION_LENGTH = 200;

  useEffect(() => {
    if (page < 1 && totalPages > 0) {
      /* In an empty list, as soon as a note is added, the page is set to be 1 */
      setActivePage(1);
    } else if (page > totalPages) {
      /* If there are 6 notes and 1 gets resolved, page 2 goes away and we need to move back to page 1 */
      setActivePage(totalPages);
    }
  }, [page, totalPages, setActivePage]);

  /*
    Check commit notes for understanding logic - git log <commit id = 505388a4b332af3146fbc687b4e84ef3c3efc286>
  */
  const notesToDisplay = useMemo(
    () =>
      notes.slice(
        (page - 1) * itemsPerPage /* Starting index */,
        page * itemsPerPage /* Ending index */
      ),
    [notes, page, itemsPerPage]
  );

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    noteId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setCurrentNoteId(noteId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentNoteId(null);
  };

  const handleMarkResolved = () => {
    if (currentNoteId) {
      updateNote({
        id: currentNoteId,
        homeSiteId,
        divisionCode,
        status: false,
        updatedBy: userId,
        opportunityId,
      });
    }
    handleMenuClose();
    showSnackbar("Note resolved successfully!", "success");
  };

  const toggleNoteExpansion = (noteId: string) => {
    setExpandedNotes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  const isNoteExpanded = (noteId: string) => expandedNotes.has(noteId);

  const shouldTruncate = (description: string) =>
    description && description.length > MAX_DESCRIPTION_LENGTH;

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        ...thinDarkScrollbar,
      }}
    >
      {notesToDisplay.map((note) => (
        <Card
          key={note.id}
          variant="outlined"
          style={{
            marginBottom: "8px",
            backgroundColor: note.status ? "#EDF4FF" : "#FFFFFF",
            border: note.status ? "default" : "1.5px solid #3F63C0",
            position: "relative",
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                backgroundColor: "#3F63C0", // Background color
                color: "white", // Text color
                fontWeight: "bold", // Bold text
                fontSize: "12px", // Font size
                padding: "4px 12px", // Padding for the pill shape
                borderRadius: "16px", // Rounded corners
                display: "inline-block", // Inline-block for text alignment
                marginBottom: "8px",
              }}
            >
              {note.mileStoneName}
            </Typography>
            <Box>
              {shouldTruncate(note.description) && !isNoteExpanded(note.id) ? (
                <Tooltip
                  title={note.description}
                  arrow
                  placement="top-end"
                  enterDelay={500}
                  leaveDelay={200}
                  sx={{
                    maxWidth: "500px",
                  }}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: note.status ? "#EDF4FF" : "#FFFFFF",
                        color: "#1C2B36",
                        fontSize: "12px",
                        maxWidth: "500px",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        border: "1px solid #E0E0E0",
                        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
                      },
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: "4px",
                      fontSize: "14px",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      cursor: "help",
                    }}
                  >
                    {note.description}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    marginBottom: shouldTruncate(note.description) ? "4px" : "8px",
                    fontSize: "14px",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {note.description}
                </Typography>
              )}
              {shouldTruncate(note.description) && (
                <Button
                  onClick={() => toggleNoteExpansion(note.id)}
                  size="small"
                  sx={{
                    textTransform: "none",
                    padding: "0",
                    minWidth: "auto",
                    fontSize: "12px",
                    color: "#3F63C0",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {isNoteExpanded(note.id) ? "Read less" : "Read more"}
                </Button>
              )}
            </Box>
            <Box display="flex" justifyContent="space-between" marginTop="8px">
              <Box display="flex" alignItems="center">
                <AccountCircleOutlined />
                <Typography variant="caption" marginLeft="8px">
                  {note.createdBy}
                </Typography>
              </Box>
              <Typography variant="caption">
                {formatDateTimeMonth(note.createdOnDate)}
              </Typography>
            </Box>
            {note.status ? (
              <IconButton
                onClick={(event) => handleMenuOpen(event, note.id)}
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "4px",
                  color: "#1C2B36",
                }}
              >
                <Tooltip title="Menu" arrow placement="left">
                  <MoreVertIcon />
                </Tooltip>
              </IconButton>
            ) : (
              <IconButton
                style={{ position: "absolute", top: "8px", right: "8px" }}
                disabled
              >
                <CheckCircleIcon sx={{ color: "green" }} />
              </IconButton>
            )}
          </CardContent>
        </Card>
      ))}
      {notes.length > itemsPerPage && (
        <Box marginTop={2} display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setActivePage(value)}
            color="primary"
          />
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MuiMenuItem onClick={handleMarkResolved}>
          <ListItemIcon>
            <CheckCircleIcon sx={{ color: "green" }} />
          </ListItemIcon>
          Mark as Resolved
        </MuiMenuItem>
      </Menu>
    </Box>
  );
};

export default NotesList;
